import Moment from 'moment';

/**
 * Calculate the patient tracking follow-up date based on business rules
 * 
 * Rules:
 * 1. Only calculate for results where recommendations.examautocompleteyn='0'
 * 2. For automatically calculated recall dates: followUpDate = recallDate + 1 day
 * 3. For null recall dates: followUpDate = resultDate
 * 
 * @param {Object} result - The patient result object
 * @param {Object} result.recommendations - Recommendations object
 * @param {string} result.recommendations.examautocompleteyn - '0' or '1'
 * @param {number|null} result.recommendations.daysuntilrecall - Days until recall or null
 * @param {string} result.resultDate - ISO date string of the result
 * @param {string} [result.recallDate] - ISO date string of the recall date (optional)
 * @returns {string|null} - ISO date string of the follow-up date, or null if not applicable
 * 
 * @example
 * // With automatic recall
 * const followUpDate = calculatePatientTrackingFollowUpDate({
 *   recommendations: { examautocompleteyn: '0', daysuntilrecall: 365 },
 *   resultDate: '2025-03-22',
 *   recallDate: '2026-03-22'
 * });
 * // Returns: '2026-03-23' (recallDate + 1 day)
 * 
 * @example
 * // Without automatic recall
 * const followUpDate = calculatePatientTrackingFollowUpDate({
 *   recommendations: { examautocompleteyn: '0', daysuntilrecall: null },
 *   resultDate: '2025-03-22'
 * });
 * // Returns: '2025-03-22' (resultDate)
 * 
 * @example
 * // Auto-complete exam (no tracking needed)
 * const followUpDate = calculatePatientTrackingFollowUpDate({
 *   recommendations: { examautocompleteyn: '1', daysuntilrecall: 365 },
 *   resultDate: '2025-03-22'
 * });
 * // Returns: null
 */
export function calculatePatientTrackingFollowUpDate(result) {
    // Only calculate for non-auto-complete exams
    if (!result || !result.recommendations || result.recommendations.examautocompleteyn !== '0') {
        return null;
    }

    // If there's an automatically calculated recall date (daysuntilrecall is not null)
    // Set follow-up to the day after recall date
    if (result.recommendations.daysuntilrecall !== null && result.recallDate) {
        return Moment(result.recallDate).add(1, 'day').format('YYYY-MM-DD');
    }

    // If there's no automatically calculated recall date, set to result date
    if (result.resultDate) {
        return Moment(result.resultDate).format('YYYY-MM-DD');
    }

    return null;
}

/**
 * Determine if a result should display patient tracking functionality
 * 
 * @param {Object} result - The patient result object
 * @param {Object} result.recommendations - Recommendations object
 * @param {string} result.recommendations.examautocompleteyn - '0' or '1'
 * @returns {boolean} - True if patient tracking should be shown
 * 
 * @example
 * const shouldShow = shouldShowPatientTracking({
 *   recommendations: { examautocompleteyn: '0' }
 * });
 * // Returns: true
 */
export function shouldShowPatientTracking(result) {
    return result && 
           result.recommendations && 
           result.recommendations.examautocompleteyn === '0';
}

/**
 * Get the most recent tracking action from a list
 * 
 * @param {Array<Object>} actions - Array of tracking actions
 * @returns {Object|null} - The most recent action or null
 */
export function getMostRecentTrackingAction(actions) {
    if (!actions || actions.length === 0) {
        return null;
    }

    return [...actions].sort((a, b) => 
        new Date(b.createdDate) - new Date(a.createdDate)
    )[0];
}

/**
 * Get the current patient tracking follow-up date
 * This checks if there are any tracking actions and uses the most recent nextFollowUpDate
 * Otherwise, calculates the initial follow-up date
 * 
 * @param {Object} result - The patient result object
 * @param {Array<Object>} [actions=[]] - Array of tracking actions
 * @returns {string|null} - ISO date string of the current follow-up date
 */
export function getCurrentFollowUpDate(result, actions = []) {
    if (!shouldShowPatientTracking(result)) {
        return null;
    }

    // Check if there's a tracking action with a nextFollowUpDate
    const recentAction = getMostRecentTrackingAction(actions);
    if (recentAction && recentAction.nextFollowUpDate) {
        return recentAction.nextFollowUpDate;
    }

    // Fall back to calculated initial date
    return calculatePatientTrackingFollowUpDate(result);
}

export default {
    calculatePatientTrackingFollowUpDate,
    shouldShowPatientTracking,
    getMostRecentTrackingAction,
    getCurrentFollowUpDate
};
